import { Loader2 } from 'lucide-react'
import * as React from 'react'
import { cn } from '@/lib/utils'

export interface EmbedLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** While true, a spinner overlay covers the embed. Children stay mounted underneath. */
  loading: boolean
  /** Optional text shown beneath the spinner (e.g. "Loading calendar…"). */
  label?: React.ReactNode
}

/**
 * Wraps a third-party embed (Calendly, Loom, an iframe, …) and shows a
 * centered spinner overlay while it loads. Children remain mounted the whole
 * time so the embed can inject/measure its own DOM behind the overlay — we
 * just cover the blank area until the host signals it's ready.
 */
const EmbedLoader = React.forwardRef<HTMLDivElement, EmbedLoaderProps>(
  ({ loading, label, className, children, ...props }, ref) => (
    <div ref={ref} className={cn('relative', className)} {...props}>
      {loading && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
          {label && <span className="text-sm text-muted-foreground">{label}</span>}
          <span className="sr-only">Loading</span>
        </div>
      )}
      {children}
    </div>
  ),
)
EmbedLoader.displayName = 'EmbedLoader'

export { EmbedLoader }
